import React, { useState, useEffect } from 'react';
import styles from './ErrorOverlay.module.css';

/**
 * A full-screen overlay that displays error messages
 * Used for critical errors like network connectivity issues
 */
const ErrorOverlay = ({ 
  errorType, 
  errorMessage, 
  onRetry,
  onClose,
  retryCount = 0,
  retryAfter = null
}) => {
  // State for countdown timer for rate limit errors
  const [countdown, setCountdown] = useState(Math.ceil((retryAfter || 0) / 1000));
  
  // Set up countdown timer for rate limit errors
  useEffect(() => {
    if (errorType === 'rate_limit' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(prevCount => {
          const newCount = prevCount - 1;
          if (newCount <= 0 && onRetry) {
            // Auto-retry when countdown reaches zero
            onRetry();
          }
          return Math.max(0, newCount);
        });
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [countdown, errorType, onRetry]);
  // Define standard error messages based on error type
  const getErrorContent = () => {
    // Base error content by type
    let content;
    
    switch (errorType) {
      case 'network':
        content = {
          title: 'Connection Error',
          message: errorMessage || 'Unable to connect to the server. This may be due to network issues or server maintenance.',
          suggestion: 'Please check your internet connection and try again in a few minutes.',
          icon: '🔌'
        };
        break;
      case 'maintenance':
        content = {
          title: 'Server Maintenance',
          message: errorMessage || 'Our servers are currently undergoing maintenance.',
          suggestion: 'Please try again later. We apologize for the inconvenience.',
          icon: '🔧'
        };
        break;
      case 'rate_limit':
        content = {
          title: 'Too Many Requests',
          message: errorMessage || 'You have made too many requests in a short period of time.',
          suggestion: countdown > 0 
            ? `Please wait ${countdown} seconds before trying again.` 
            : 'You can try again now.',
          icon: '⏱️'
        };
        break;
      case 'server':
        content = {
          title: 'Server Error',
          message: errorMessage || 'The server encountered an error while processing your request.',
          suggestion: 'Please try again later. We are working to resolve the issue.',
          icon: '🛠️'
        };
        break;
      case 'auth':
        content = {
          title: 'Authentication Error',
          message: errorMessage || 'Your session has expired or is invalid.',
          suggestion: 'Please log in again to continue.',
          icon: '🔑'
        };
        break;
      default:
        content = {
          title: 'Unexpected Error',
          message: errorMessage || 'Something went wrong.',
          suggestion: 'Please try again. If the problem persists, contact support.',
          icon: '⚠️'
        };
    }
    
    // Modify suggestion based on retry count for network and maintenance errors
    if ((errorType === 'network' || errorType === 'maintenance') && retryCount >= 3) {
      content.suggestion = 'We\'ve tried several times but still can\'t connect. The server might be down or undergoing maintenance. Please try again later.';
    }
    
    return content;
  };

  const errorContent = getErrorContent();

  return (
    <div className={styles.overlay}>
      <div className={styles.errorCard}>
        <div className={styles.errorIcon}>{errorContent.icon}</div>
        <h2 className={styles.errorTitle}>{errorContent.title}</h2>
        <p className={styles.errorMessage}>{errorContent.message}</p>
        <p className={styles.errorSuggestion}>{errorContent.suggestion}</p>
        <div className={styles.errorActions}>
          {onRetry && (
            <button 
              className={styles.retryButton} 
              onClick={onRetry}
              disabled={errorType === 'rate_limit' && countdown > 0}
            >
              {errorType === 'rate_limit' && countdown > 0
                ? `Retry in ${countdown}s`
                : retryCount > 0 
                  ? `Try Again (${retryCount}/3)` 
                  : 'Try Again'}
            </button>
          )}
          {!onRetry && retryCount >= 3 && (errorType === 'network' || errorType === 'maintenance' || errorType === 'server') && (
            <div className={styles.retryLimitMessage}>
              Maximum retry attempts reached
            </div>
          )}
          {onClose && (
            <button 
              className={styles.closeButton} 
              onClick={onClose}
            >
              {errorType === 'auth' ? 'Log In Again' : 'Close'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorOverlay;
